import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ICON_POSITIONS,
  ICON_TYPES,
} from '../../../shared/constants';
import { WIZARD_RESULT } from '../../../shared/constants/Wizard.constants';
import {
  ArticleQueryItem,
  ProductQueryItem,
  Tag,
} from '../../../types/shared/shared.types';

import { languageHelper } from '../../../utils/language';
import { LABELS } from '../../../utils/language/product-card';
import {
  getMainTags,
  getSubTags,
  getTagsByTypes,
  sortTagsAlphabetically,
} from '../../../utils/tags';

import { TagTypes } from '../../../tokens';

import {
  ParsedArticle,
  ParsedProduct,
  ParsedSection,
  ParseSectionsParameter,
} from './WizardResultOverview.types';

// Return the URL for the magazine overview, with selected tag value.
const getFilteredArticlesURL = (tag: Tag) =>
  `/magazine?filter=${tag.type}_${tag.value}`;

// Parse string from query parameter to tags found to matching tags from the CMS.
const parseQueryParameterToTags = (query: string, tags: Tag[]): Tag[] => {
  const foundValues = query.split(',');
  const tagValues: (Tag | undefined)[] = foundValues.map((value, index) => {
    const tagType = value.split('_')[0] || 'unknown-type';
    const tagValue = value.split('_')[1] || 'unknown-value';

    return tags.find((tag) => tag.type === tagType && tag.value === tagValue);
  });

  return tagValues.filter(Boolean) as Tag[];
};

// Retuns true if the product page or article page contains the "main" (category) tag in combination with the passed "sub" tags.
const hasMatchingMainAndSubTags = (
  item: ArticleQueryItem | ProductQueryItem,
  mainTagValue: string,
  subTags: Tag[]
): boolean => {
  const mainTagMatches = !!item.tags?.find((tag) => tag.value === mainTagValue);
  const subTagMatches = !!item.tags?.find((tag) =>
    subTags.find((subTag) => subTag.value === tag.value)
  );

  // When the main tag is matching, but there are no subtags selected in the options.
  if (mainTagMatches && !subTags.length) {
    return true;
  }

  return mainTagMatches && subTagMatches;
};

// Returns true if passed item has all of the provided segment tags.
const hasAllSegmentTags = (
  item: ArticleQueryItem | ProductQueryItem,
  segmentTags: Tag[]
) =>
  segmentTags.every((tag) => item.tags?.some((value) => value.id === tag.id));

// Return the data needed for the product result card based on the queried data, filtered by tags.
const parseProducts = (
  mainTagValue: string,
  subTags: Tag[],
  segmentTags: Tag[],
  products: ProductQueryItem[]
): ParsedProduct[] => {
  const foundProducts: ProductQueryItem[] = products
    .filter((product) => !!product.overviewCard)
    .filter((product) =>
      hasMatchingMainAndSubTags(product, mainTagValue, subTags)
    )
    .filter((product) => hasAllSegmentTags(product, segmentTags));

  const { lang } = languageHelper();

  const parsedProducts = foundProducts.map((product) => ({
    title: product.overviewCard?.title || '',
    body: product.overviewCard?.body || {
      childMarkdownRemark: { rawMarkdownBody: '' },
    },
    primaryLink: {
      icon: ICON_TYPES.chevron,
      iconPosition: ICON_POSITIONS.end,
      theme: BUTTON_THEMES.default,
      label: LABELS.primaryLink[lang],
      type: BUTTON_TYPES.internal,
      linkedPage: {
        slug: product.slug,
        parentPage: product.parentPage,
      },
      name: product.pageName || '',
    },
    image: product.overviewCard?.image,
  }));

  return parsedProducts.slice(0, WIZARD_RESULT.maxProducts);
};

// Return the data needed for the article preview card based on the queried data, filtered by tags.
const parseArticles = (
  mainTagValue: string,
  subTags: Tag[],
  segmentTags: Tag[],
  articles: ArticleQueryItem[]
): ParsedArticle[] => {
  const foundArticles = articles
    .filter((article) =>
      hasMatchingMainAndSubTags(article, mainTagValue, subTags)
    )
    .filter((article) => hasAllSegmentTags(article, segmentTags));

  const parsedArticles = foundArticles.map((article) => ({
    tags: getTagsByTypes(article.tags || [], [
      TagTypes.SECTOR,
      TagTypes.CATEGORY,
    ]),
    title: article.title,
    body: article.lead?.lead,
    slug: article.slug,
    parentPage: article.parentPage,
    image: article.mainImage,
    publishingTime: article.publishingTime,
  }));

  return parsedArticles.slice(0, WIZARD_RESULT.maxArticles);
};

const parseSectionsData = ({
  tags,
  products,
  articles,
}: ParseSectionsParameter): ParsedSection[] => {
  const mainTags = sortTagsAlphabetically(getMainTags(tags));
  const subTags = getSubTags(tags);
  const segmentTags = getTagsByTypes(tags, [TagTypes.SEGMENT]);

  return mainTags
    .map((tag) => ({
      tag,
      products: parseProducts(tag.value, subTags, segmentTags, products),
      articles: parseArticles(tag.value, subTags, segmentTags, articles),
    }))
    .filter((data) => data.articles.length || data.products.length);
};

export { getFilteredArticlesURL, parseQueryParameterToTags, parseSectionsData };
